// platformAdminGuard.ts — route-level guard для PA-only роутов (PA-4,
// D16 §6). Если в JWT нет is_platform_admin=true → throw redirect на
// /dashboard (distributor-scoped landing).
//
// React context (useDistributionPrincipal) недоступен в beforeLoad, поэтому
// мы читаем claim напрямую из user-state oidc-client-ts. PA-6 fix: реальный
// `@dss/auth` AuthProvider конфигурирует `WebStorageStateStore` поверх
// `window.localStorage`, поэтому oidc-client-ts кладёт user под ключом
// `oidc.user:<authority>:<client_id>` именно в localStorage. Ранее guard
// читал из sessionStorage, и любой переход на /distributors редиректил
// PA обратно на /dashboard. Этот же приём используется в routes/index.tsx
// для conditional redirect с `/`.

import { redirect } from '@tanstack/react-router'

type RedirectFn = typeof redirect

/** Бросает redirect на /dashboard, если пользователь не PLATFORM_ADMIN.
 *  Принимает функцию `redirect` от вызывающего, чтобы избежать дубля import'а
 *  в route-файлах (TanStack-стандарт — импортировать redirect в самом роуте). */
export function redirectIfNotPlatformAdmin(redirectFn: RedirectFn = redirect): void {
  if (!isPlatformAdminFromStorage()) {
    throw redirectFn({ to: '/dashboard' })
  }
}

/** Прочитать `is_platform_admin` из oidc-client-ts user'а в localStorage.
 *
 *  oidc-client-ts по умолчанию хранит user в sessionStorage, но `@dss/auth`
 *  AuthProvider явно настраивает `WebStorageStateStore({ store: window.localStorage })`
 *  — поэтому единственный валидный источник здесь именно localStorage. */
export function isPlatformAdminFromStorage(): boolean {
  if (typeof localStorage === 'undefined') return false
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (!key || !key.startsWith('oidc.user:')) continue
    try {
      const raw = localStorage.getItem(key)
      if (!raw) continue
      const parsed = JSON.parse(raw) as { profile?: { is_platform_admin?: unknown } }
      const claim = parsed?.profile?.is_platform_admin
      if (typeof claim === 'boolean') return claim
      if (typeof claim === 'string') return claim.toLowerCase() === 'true'
    } catch {
      // повреждённый JSON — пропускаем
    }
  }
  return false
}
